import { widgetFilterSchema } from '../../common/schema'
import { z } from 'zod'
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import { usePropertyDetails, type PropertyDetailsRaw } from '../../common/property-details'
import { fetchDescription } from '../description/data'

export const propertyTitleParamsSchema = widgetFilterSchema.extend({
  feed_id: z.number().int().describe('Filter by feed ID'),
  platform: z.string().nullish().describe('Filter by platform UUID'),
  property: z.string().describe('Filter by property slug'),
}).describe('Data filter options for property title')

export type PropertyTitleFilter = z.infer<typeof propertyTitleParamsSchema>

export type PropertyTitle = {
  /**
   * Human-readable display/marketing name (#998, #1618). `raw.name` is the
   * internal nickname/slug-style identifier (e.g. "demo-cedar-ridge-retreat-1"),
   * and on Guesty feeds Central's `/property-details` returns `raw.title` EQUAL
   * to it, so preferring `title` (#1296) never changed the live H1. The
   * marketing name lives on the property's description record, so the widget
   * resolves exactly the chain the renderer's meta title / og:title / JSON-LD
   * VacationRental.name / synthesized H1 already use
   * (property-schema.ts resolvePropertyDisplayName):
   *   descriptions[0].name → title → name
   */
  displayName: string
  type: { id: number; name: string } | null
  city: { id: number; name: string } | null
  rating?: { average: number; count: number } | null
}

/**
 * Same chain as the renderer's `resolvePropertyDisplayName` — the two must
 * agree or the page's H1 and its `<title>`/JSON-LD name drift apart (#1618).
 */
export function resolvePropertyDisplayName(
  raw: Pick<PropertyDetailsRaw, 'name' | 'title'>,
  descriptionName?: string | null,
): string {
  return descriptionName?.trim() || raw.title?.trim() || raw.name
}

export function selectPropertyTitle(raw: PropertyDetailsRaw, descriptionName?: string | null): PropertyTitle {
  return {
    displayName: resolvePropertyDisplayName(raw, descriptionName),
    type: raw.type,
    city: raw.city,
    rating: raw.rating,
  }
}

/**
 * The description widget's query (#1618): the SAME key shape as
 * `widgets/description/data.ts` `useFetchDescription` so a PDP that renders
 * both widgets fetches `/property-descriptions` once (SSR prefetch included),
 * and the SAME queryFn so whichever observer mounts first the request keeps
 * its `okOrThrow` error envelope.
 */
export function descriptionKey(filter: PropertyTitleFilter) {
  return ['description', filter] as const
}

export function selectDescriptionName(data: Awaited<ReturnType<typeof fetchDescription>>): string | null {
  return data.result?.descriptions?.[0]?.name ?? null
}

/**
 * How long a client mount keeps holding the heading for the description once
 * the details have answered. The description is an enrichment, not a
 * prerequisite: past this, the heading falls back to title → name and a late
 * answer re-projects it. Bounds a hung `/property-descriptions` (no fetch
 * timeout) and the runtime client's retry backoff on a 5xx, which would
 * otherwise pin a CSR-only embed on "Loading..." indefinitely.
 */
export const DESCRIPTION_NAME_DEADLINE_MS = 3000

const noopSubscribe = () => () => {}

export function useFetchPropertyTitle(filter: PropertyTitleFilter) {
  const description = useQuery({
    queryKey: descriptionKey(filter),
    queryFn: () => fetchDescription(filter),
    select: selectDescriptionName,
  })
  const descriptionName = description.data ?? null
  const select = useCallback(
    (raw: PropertyDetailsRaw) => selectPropertyTitle(raw, descriptionName),
    [descriptionName],
  )
  const details = usePropertyDetails(filter, { select })

  // Hold the heading until BOTH answers are in, so a CSR mount never paints
  // the internal name and then flips to the marketing name a beat later. The
  // hold applies to the description's FIRST answer only: `isFetched` is true
  // after the first success OR error, and it must be — React Query's
  // optimistic mount result reports a data-less errored query as
  // pending-and-fetching whenever it would retry on mount, so gating on
  // `isLoading` alone would (a) bake "Loading..." into cached SSR HTML
  // whenever the description prefetch failed but the details one succeeded
  // (the #945 class; the server gets exactly one render) and (b) flip an
  // already-rendered heading back to "Loading..." when a later observer —
  // the description dialog mounting — refetches a once-failed description.
  const detailsReady = details.data !== undefined
  const descriptionPending = description.isLoading && !description.isFetched

  // The hold is per property (a live `options-updated` can swap the slug
  // under this same hook instance) and it only ARMS on a NON-hydrating render
  // whose description has not settled yet — i.e. a real client mount (or a
  // live switch) that is about to wait for a description. Whether the
  // details happen to be cached already (a sibling property-meta/map widget
  // warmed them, a remount, a second title widget) makes no difference: the
  // nickname must never paint first. A hydrating render never arms: the
  // server rendered without a hold, and if its description prefetch had
  // failed (errored queries are not dehydrated) the client's first render
  // must equal the server's title → name fallback rather than "Loading...",
  // or React reports a hydration mismatch. React itself tells us which
  // render this is — useSyncExternalStore hands the server snapshot to the
  // server render AND to the hydration render, the client snapshot to
  // everything else. The hold is also bounded (DESCRIPTION_NAME_DEADLINE_MS)
  // from the moment the details are in; effects never run on the server.
  const isHydratingRender = useSyncExternalStore(noopSubscribe, () => false, () => true)
  const descriptionSettled = description.data !== undefined || description.isFetched
  const holdKey = JSON.stringify(descriptionKey(filter))
  const [hold, setHold] = useState(() => ({ key: holdKey, armed: !isHydratingRender && !descriptionSettled, deadlinePassed: false }))
  if (hold.key !== holdKey) {
    // New property under a live instance: start that property's own hold.
    setHold({ key: holdKey, armed: !isHydratingRender && !descriptionSettled, deadlinePassed: false })
  }
  const holdActive = hold.key === holdKey && hold.armed && !hold.deadlinePassed && detailsReady && descriptionPending
  useEffect(() => {
    if (!holdActive) return
    const timer = setTimeout(
      () => setHold((h) => (h.key === holdKey ? { ...h, deadlinePassed: true } : h)),
      DESCRIPTION_NAME_DEADLINE_MS,
    )
    return () => clearTimeout(timer)
  }, [holdActive, holdKey])

  return {
    data: details.data,
    isLoading: details.isLoading || holdActive,
    isError: details.isError,
  }
}
